import React, { useEffect, useRef, useState } from "react";
import { Button } from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import trans from "../../utils";
import { resolveRuntimeValues } from "../FormBuilder/runtimeValues";

export default function RepeatingGroupPreview({ getInputType, item, value, onValueChange }) {
	const groupConfig = item?.groupConfig || {};
	const initialCount = Math.max(groupConfig.minItems || 0, 1);
	const nextItemKey = useRef(initialCount);
	const [items, setItems] = useState(() => {
		const submittedItems = value?.items;
		if (Array.isArray(submittedItems) && submittedItems.length > 0) {
			return submittedItems.map((values, index) => ({
				key: index,
				collapsed: false,
				values,
			}));
		}
		return Array.from({ length: initialCount }, (_, index) => ({
			key: index,
			collapsed: false,
			values: {},
		}));
	});
	const onValueChangeRef = useRef(onValueChange);
	onValueChangeRef.current = onValueChange;

	useEffect(() => {
		onValueChangeRef.current?.(item.key, {
			items: items.map((groupItem) =>
				resolveRuntimeValues(item.fields || [], groupItem.values)
			),
		});
	}, [items, item.key, item.fields]);

	const addItem = () => {
		setItems((prev) => [
			...prev,
			{ key: nextItemKey.current++, collapsed: false, values: {} },
		]);
	};

	const removeItem = (index) => {
		setItems((prev) => prev.filter((_, itemIndex) => itemIndex !== index));
	};

	const toggleCollapse = (index) => {
		setItems((prev) =>
			prev.map((groupItem, itemIndex) =>
				itemIndex === index
					? { ...groupItem, collapsed: !groupItem.collapsed }
					: groupItem
			)
		);
	};

	const updateItemValue = (index, fieldKey, value) => {
		setItems((prev) =>
			prev.map((groupItem, itemIndex) =>
				itemIndex === index
					? {
							...groupItem,
							values: resolveRuntimeValues(item.fields || [], {
								...groupItem.values,
								[fieldKey]: value,
							}),
					  }
					: groupItem
			)
		);
	};

	return (
		<div style={{ border: "1px dashed #ccc", padding: 12, borderRadius: 6 }}>
			<h5 style={{ marginBottom: 12 }}>
				{groupConfig.groupTitle || trans("Repeating Group")}
			</h5>
			{items.map((groupItem, index) => {
				const values = resolveRuntimeValues(item.fields || [], groupItem.values);
				return (
					<div
						key={groupItem.key}
						style={{
							marginBottom: 12,
							border: "1px solid #eee",
							borderRadius: 6,
							padding: 12,
						}}
					>
						<div
							className="d-flex justify-content-between align-items-center"
							style={{ marginBottom: 8 }}
						>
							<span>
								{groupConfig.itemLabel || trans("Item")} {index + 1}
							</span>
							<div>
								{groupConfig.collapsibleItems && (
									<Button type="link" onClick={() => toggleCollapse(index)}>
										{groupItem.collapsed ? trans("Expand") : trans("Collapse")}
									</Button>
								)}
								{groupConfig.allowDelete &&
									items.length > (groupConfig.minItems || 0) && (
										<Button
											type="text"
											danger
											icon={<DeleteOutlined />}
											onClick={() => removeItem(index)}
										/>
									)}
							</div>
						</div>

						{(!groupItem.collapsed || !groupConfig.collapsibleItems) && (
							<div>
								{(item.fields || []).map((child) => (
									<div key={child.key} style={{ marginBottom: 8 }}>
										{getInputType(child, {
											fields: item.fields || [],
											values,
											onValueChange: (fieldKey, value) =>
												updateItemValue(index, fieldKey, value),
										})}
									</div>
								))}
							</div>
						)}
					</div>
				);
			})}

			<Button
				type="dashed"
				block
				icon={<PlusOutlined />}
				onClick={addItem}
				disabled={groupConfig.maxItems != null && items.length >= groupConfig.maxItems}
			>
				{groupConfig.addItemLabel || trans("Add Item")}
			</Button>
		</div>
	);
}
